/**
 * components/quotes/AngularDimension.tsx — Quota angolare (MUTA, PLAN quote v1).
 *
 * Riceve un `AngularQuote` GIA' risolto (vertice + due direzioni unitarie) e
 * disegna: due brevi linee dal vertice lungo le direzioni, un arco campionato
 * fra le due, due frecce TANGENTI all'arco (convenzione da goniometro:
 * puntano verso l'esterno, non radialmente) e l'etichetta a meta' angolo,
 * spostata radialmente oltre l'arco. Nessun calcolo di geometria del pezzo —
 * solo algebra di posizionamento (SPEC §6, AC-7: components/ MUTO).*/

import { Line } from "@react-three/drei";
import { useMemo } from "react";
import * as THREE from "three";
import { COLORS } from "../../config/colors";
import { QUOTE_DEFAULTS } from "../../config/defaults";
import type { Vec3 } from "../../data/types";
import type { AngularQuote } from "../../model/quotes.types";
import { Arrowhead } from "./Arrowhead";
import { QuoteLabel } from "./QuoteLabel";

export interface AngularDimensionProps {
  quote: AngularQuote;
}

/** Render della quota angolare dall'`AngularQuote` risolto.*/
export function AngularDimension({ quote }: AngularDimensionProps) {
  const geometry = useMemo(() => buildAngularGeometry(quote), [quote]);
  if (!geometry) return null;

  return (
    <group>
      <Line points={geometry.legA} color={COLORS.quoteExtensionLine} />
      <Line points={geometry.legB} color={COLORS.quoteExtensionLine} />
      <Line points={geometry.arcPoints} color={COLORS.quoteAngular} />
      <Arrowhead tip={geometry.arrowATip} direction={geometry.arrowADir} color={COLORS.quoteAngular} />
      <Arrowhead tip={geometry.arrowBTip} direction={geometry.arrowBDir} color={COLORS.quoteAngular} />
      <QuoteLabel position={geometry.labelPos} text={quote.label} />
    </group>
  );
}

//─── Derivazione pura della geometria di disegno ─────────────────

interface AngularDimensionGeometry {
  legA: [THREE.Vector3, THREE.Vector3];
  legB: [THREE.Vector3, THREE.Vector3];
  arcPoints: THREE.Vector3[];
  arrowATip: Vec3;
  arrowADir: Vec3;
  arrowBTip: Vec3;
  arrowBDir: Vec3;
  labelPos: Vec3;
}

/**
 * Calcola le primitive di disegno da vertice + due direzioni. L'asse di
 * rotazione e' `dirA × dirB` (right-hand rule): campionare con angoli
 * crescenti su quest'asse porta da dirA a dirB senza dipendere dal segno di
 * `angleDeg` (che serve solo all'etichetta, gia' formattata a monte).
 * Ritorna `null` per direzioni degeneri o coincidenti (nessun angolo da
 * disegnare).*/
function buildAngularGeometry(quote: AngularQuote): AngularDimensionGeometry | null {
  const vertex = new THREE.Vector3(...quote.vertex);
  const dirA = new THREE.Vector3(...quote.dirA);
  const dirB = new THREE.Vector3(...quote.dirB);
  if (dirA.lengthSq() < 1e-12 || dirB.lengthSq() < 1e-12) return null;
  dirA.normalize();
  dirB.normalize();

  const axis = new THREE.Vector3().crossVectors(dirA, dirB);
  if (axis.lengthSq() < 1e-12) return null; //direzioni parallele/coincidenti → nessun angolo
  axis.normalize();

  const angleRad = Math.acos(THREE.MathUtils.clamp(dirA.dot(dirB), -1, 1));
  const radius = quote.radiusMm ?? QUOTE_DEFAULTS.arcRadiusMm;
  const overshoot = QUOTE_DEFAULTS.extensionOvershootMm;
  const segments = QUOTE_DEFAULTS.arcSegments;

  const pointAt = (t: number) =>
    dirA.clone().applyAxisAngle(axis, t * angleRad).multiplyScalar(radius).add(vertex);
  const tangentAt = (t: number) =>
    axis.clone().cross(dirA.clone().applyAxisAngle(axis, t * angleRad)).normalize();

  const arcPoints: THREE.Vector3[] = [];
  for (let i = 0; i <= segments; i++) arcPoints.push(pointAt(i / segments));

  //Ricalcolati invece di indicizzare arcPoints (noUncheckedIndexedAccess):
  //coincidono con arcPoints[0]/arcPoints[segments] per costruzione.
  const start = pointAt(0);
  const end = pointAt(1);
  const tangentStart = tangentAt(0);
  const tangentEnd = tangentAt(1);

  const midDir = dirA.clone().applyAxisAngle(axis, 0.5 * angleRad);
  const labelPoint = vertex.clone().addScaledVector(midDir, radius + QUOTE_DEFAULTS.offsetMm * 0.5);

  return {
    legA: [vertex, vertex.clone().addScaledVector(dirA, radius + overshoot)],
    legB: [vertex, vertex.clone().addScaledVector(dirB, radius + overshoot)],
    arcPoints,
    //Convenzione goniometro: le frecce puntano verso l'esterno dell'arco
    //(tangenti, non radiali), come le quote lineari puntano oltre i punti misurati.
    arrowATip: [start.x, start.y, start.z],
    arrowADir: [-tangentStart.x, -tangentStart.y, -tangentStart.z],
    arrowBTip: [end.x, end.y, end.z],
    arrowBDir: [tangentEnd.x, tangentEnd.y, tangentEnd.z],
    labelPos: [labelPoint.x, labelPoint.y, labelPoint.z],
  };
}
